import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { useMetaForProject } from '@/layout/Seo/ProjectSeoHelper'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import {
  Download,
  getPartnershipDownloadsByProjectSlug,
  getPartnershipPageContentByProject,
} from '@/services/PartnershipService/PartnershipService'
import { getProjectAbout, ProjectMeta } from '@/services/ProjectsService'
import { EmbeddedVideo, Project } from '@/types/codegen-contentful'
import { useLocale } from '@/utils/LocaleUtil'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PartnershipsAssetsView } from '@/views/PartnershipsView'

interface PartnershipsAssetsProjectPageProps {
  assets: Download[]
  keyArt: Download[]
  keyImages: Download[]
  project: Project
  projectMeta: ProjectMeta
  trailers: Download[]
  videos: EmbeddedVideo[]
}

const PartnershipsAssetsProjectPage: NextPageWithLayout<PartnershipsAssetsProjectPageProps> = ({
  assets,
  keyArt,
  keyImages,
  project,
  projectMeta,
  trailers,
  videos,
}) => {
  const { locale } = useLocale()
  const meta = useMetaForProject({ ...projectMeta, locale })

  const url = useCanonicalUrl(`${paths.assets.partnerships.index}/${project.slug}`)
  const title = `${project.name} Partnership Assets | Angel`
  const description = `${project.name} Partnership Kit`

  return (
    <>
      <Seo {...meta} canonical={url} title={title} description={description} />
      <PartnershipsAssetsView
        assets={assets}
        keyArt={keyArt}
        keyImages={keyImages}
        project={project}
        trailers={trailers}
        videos={videos}
      />
    </>
  )
}

PartnershipsAssetsProjectPage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<PartnershipsAssetsProjectPageProps>(
  { filePath: 'pages/[locale]/assets/partnerships/[project].tsx' },
  async ({ federationClient, locale, next404, params, preview }) => {
    const projectSlug = getItemOrFirstEntry(params?.project)
    if (!projectSlug) return next404()

    const [{ project, videos }, { assets, trailers, keyArt, keyImages }, projectMeta, translations] = await Promise.all(
      [
        getPartnershipPageContentByProject(preview, projectSlug, locale),
        getPartnershipDownloadsByProjectSlug(preview, projectSlug, locale),
        getProjectAbout({ slug: projectSlug }, federationClient, { locale, preview }),
        loadTranslations(locale, ['common', 'account', 'watch', 'partnership']),
      ],
    )

    if (!project.hasPartnershipPage) return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    if (!projectMeta) return next404()

    return {
      props: {
        assets,
        keyArt,
        keyImages,
        project,
        projectMeta,
        trailers,
        videos,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default PartnershipsAssetsProjectPage
